import type { AnyEditor } from './aiChatShared';

export function getTimeGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Goedemorgen';
  if (h < 18) return 'Goedemiddag';
  return 'Goedenavond';
}

export function extractDocumentContext(editor: AnyEditor): string {
  const pages: string[] = [];
  let index = 0;
  editor.state.doc.forEach((node: any) => {
    index++;
    const typeName = node.type.name;
    const attrs = node.attrs || {};
    const blockType = attrs.blockType || attrs.type || typeName;
    const text = node.textContent.trim();
    if (!text) {
      pages.push(`[Pagina ${index} | ${blockType} — geen tekst]`);
      return;
    }
    pages.push(`[Pagina ${index} | ${blockType}]\n${text}`);
  });
  return pages.join('\n\n');
}

export function getBlockIdForPage(editor: AnyEditor, pageNumber: number): string | null {
  let index = 0;
  let blockId: string | null = null;
  editor.state.doc.forEach((node: any) => {
    index++;
    if (index === pageNumber && !blockId) {
      blockId = node.attrs?.id || null;
    }
  });
  return blockId;
}

export function scrollToBlockId(blockId: string) {
  const el = document.querySelector(`[data-block-id="${blockId}"]`);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-2', 'ring-blue-400/60', 'ring-offset-2');
    setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400/60', 'ring-offset-2'), 2000);
  }
}

export function closeOpenTags(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    return doc.body.firstElementChild?.innerHTML || html;
  } catch {
    return html;
  }
}

export function clearFloatingBadge(): void {
  const badge = document.getElementById('ai-floating-badge');
  if (badge) {
    badge.classList.add('ai-badge-exit');
    setTimeout(() => badge.remove(), 300);
  }
}

export function setBlockAiEditing(blockId: string): void {
  const el = document.querySelector(`[data-block-id="${blockId}"]`);
  if (!el) return;

  el.classList.add('ai-editing');
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });

  clearFloatingBadge();
  const badge = document.createElement('div');
  badge.id = 'ai-floating-badge';
  badge.className = 'ai-floating-badge';
  badge.innerHTML = `
    <div class="ai-floating-badge-inner">
      <span class="ai-floating-dot"></span>
      <span>AI Agent bewerkt pagina</span>
    </div>
  `;
  document.body.appendChild(badge);

  const positionBadge = () => {
    if (!badge.isConnected || !el.classList.contains('ai-editing')) return;
    const rect = el.getBoundingClientRect();
    badge.style.top = `${Math.max(8, rect.top - 44)}px`;
    badge.style.left = `${rect.left}px`;
    requestAnimationFrame(positionBadge);
  };
  requestAnimationFrame(positionBadge);
}

export function clearBlockAiEditing(blockId: string): void {
  const el = document.querySelector(`[data-block-id="${blockId}"]`);
  if (el) {
    el.classList.remove('ai-editing');
    el.classList.add('ai-edit-done');
    setTimeout(() => el.classList.remove('ai-edit-done'), 2500);
  }
  clearFloatingBadge();
}

export function clearAllAiEditing(): void {
  document.querySelectorAll('.ai-editing').forEach((el) => el.classList.remove('ai-editing'));
  document.querySelectorAll('.ai-edit-done').forEach((el) => el.classList.remove('ai-edit-done'));
  document.getElementById('ai-floating-badge')?.remove();
}

export function linkifyPageRefs(content: string): string {
  return content.replace(/(?<!\[)[Pp]agina\s+(\d+)(?!\])/g, '[Pagina $1](#page-$1)');
}

export function replacePageContent(editor: AnyEditor, pageIndex: number, newText: string): boolean {
  let currentIndex = 0;
  let targetPos: number | null = null;
  let targetNodeSize: number | null = null;

  editor.state.doc.forEach((node: any, offset: number) => {
    if (targetPos !== null) return;
    currentIndex++;
    if (currentIndex === pageIndex) {
      targetPos = offset;
      targetNodeSize = node.nodeSize;
    }
  });

  if (targetPos === null || targetNodeSize === null) return false;

  const innerStart = targetPos + 1;
  const innerEnd = targetPos + targetNodeSize - 1;

  const paragraphs = newText.split('\n').filter((line) => line.trim());
  const content = paragraphs.map((p) => ({
    type: 'paragraph' as const,
    content: [{ type: 'text' as const, text: p }],
  }));

  if (content.length === 0) return false;

  editor
    .chain()
    .focus()
    .command(({ tr }: { tr: any }) => {
      tr.replaceWith(
        innerStart,
        innerEnd,
        content.map((c) => editor.state.schema.nodeFromJSON(c)),
      );
      return true;
    })
    .run();

  return true;
}

export function chatStorageKey(quoteId: string) {
  return `ai-chat-${quoteId}`;
}

export function buildPageContent(input: Record<string, any>): Record<string, unknown> {
  const type = input.page_type as string;
  const title = (input.title as string) || '';
  const html = (input.content_html as string) || '';

  switch (type) {
    case 'COVER':
      return { title: title || '{{deal.name}}', subtitle: 'Groeiprogramma', theme: 'ubo' };
    case 'CONTENT_PAGE':
      return {
        title,
        content: html || `<h1>${title || 'Nieuwe pagina'}</h1><p>Start hier met je tekst...</p>`,
        style: 'default',
      };
    case 'TEAM':
      return { title: title || 'Jouw team', selectedMembers: [], members: [] };
    case 'PLAN_VAN_AANPAK':
      return {
        title: title || 'Plan van Aanpak',
        content: html || '<p>Beschrijf hier het plan van aanpak...</p>',
      };
    case 'SIGNATURE_PAGE':
      return { preText: 'Hierbij gaan wij akkoord met de voorwaarden.', showSignature: true };
    case 'FOOTER':
      return { headline: title || 'Bedankt voor uw vertrouwen', theme: 'ubo' };
    default:
      return { title, content: html };
  }
}

export function buildInvestmentContent(input: Record<string, any>): Record<string, unknown> {
  const tables = (input.tables as any[]) || [];
  return {
    mainTitle: (input.main_title as string) || 'Investeringsoverzicht',
    tables: tables.map((t: any, ti: number) => ({
      id: `ai-table-${Date.now()}-${ti}`,
      title: t.title || 'Investeringsoverzicht',
      pricingType: t.pricing_type || 'fixed',
      rows: (t.rows || []).map((r: any, ri: number) => ({
        id: `ai-row-${Date.now()}-${ti}-${ri}`,
        title: r.title || '',
        price: r.price,
        hours: r.hours,
        subtotal: r.subtotal ?? (r.price && r.hours ? r.price * r.hours : 0),
      })),
      fixedTotal: t.fixed_total,
    })),
  };
}
